// DL-452 — Constants for the Academy iframe embed in Horizon.
// Everything hanging off this file is deleted with the native migration (DL-385..DL-398).

// Prefix of the public embed routes. This is the boot-time key: `Sentry.init` and App.vue's
// `created()` both run before the router resolves its first navigation, so `meta.embed` is not
// readable there yet (see src/utils/embedPath.js).
export const EMBED_PATH_PREFIX = "/embed";

// Parent origins allowed to embed Academy, as anchored patterns rather than a fixed list.
//
// Horizon is multi-tenant: an organisation with its own subdomain is served from
// `{org}.yourney.io`, and only the standard tier uses `app.yourney.io`. A fixed list of two hosts
// therefore locked out every organisation with a subdomain — the `ready` never reached the parent
// and the parent's token was rejected. Same shape as core's CORS config
// (`config/settings/staging.yml`), which solves this for the same reason.
//
// Anchored at both ends on purpose: without `^` and `$`, `app.yourney.io.evil.com` matches. The
// label is `[\w-]+`, a single level, so a nested `a.b.yourney.io` does not match and neither does
// the apex — the marketing site is not a tenant.
//
// Both domains are allowed in every environment, including production: in phase 2 Horizon staging
// (`app.yourneydev.com`) embeds production Academy, so narrowing production to `yourney.io` would
// break it.
//
// Where this ends: any subdomain of these two domains can embed Academy and hand it a token. They
// are all ours, so the trade-off is accepted — but a dangling DNS record on a subdomain becomes an
// embedding vector, which a fixed list did not allow.
export const EMBED_PARENT_ORIGIN_PATTERNS = [
  /^https:\/\/[\w-]+\.yourney\.io$/,
  /^https:\/\/[\w-]+\.yourneydev\.com$/,
];

// Horizon dev runs on port 5173 with host: true, on the .lvh.me domain. These are NOT compiled into
// production: `lvh.me` is a public wildcard domain resolving to 127.0.0.1 whose DNS we do not
// control, and both are plaintext HTTP. With them allowed, any process the victim runs on that port
// becomes authorised to hand a JWT to the production embed.
const DEV_PARENT_ORIGINS = ["http://app.lvh.me:5173", "http://localhost:5173"];

export const EMBED_EXACT_PARENT_ORIGINS =
  process.env.NODE_ENV === "production" ? [] : DEV_PARENT_ORIGINS;

// postMessage protocol v1: every message, in both directions, carries `source` and `version`.
export const EMBED_MESSAGE_SOURCE = "yourney-academy-embed";
export const EMBED_PROTOCOL_VERSION = 1;

// iframe -> parent
export const EMBED_OUTBOUND = {
  READY: "ready",
  TOKEN_EXPIRED: "token_expired",
  HEIGHT: "height",
  EVENT: "event",
  ERROR: "error",
};

// parent -> iframe
export const EMBED_INBOUND = {
  TOKEN: "token",
};

// Names fixed by the product event catalog: do not change them without telling Horizon, which
// republishes them in its PostHog. Horizon validates name and props against a literal allowlist
// (its taxonomy guard only protects the first literal argument of capture()), so any name or prop
// outside this contract is silently discarded on the other side.
//
// `academy_opened` is deliberately NOT here: Horizon emits it. It is the "entered the section"
// event and the parent is its source of truth — emitted by the iframe it would read zero exactly
// when the iframe fails to load, which is the case we most want to measure.
export const EMBED_EVENTS = {
  CONTENT_OPENED: "academy_content_opened",
  COLLECTION_OPENED: "academy_collection_opened",
};

// Allowed props per event. Filtered before posting: sending anything extra is not a visible
// error (Horizon discards it) but it leaves the event unpublished.
export const EMBED_EVENT_PROPS = {
  [EMBED_EVENTS.CONTENT_OPENED]: ["content_id", "type"],
  [EMBED_EVENTS.COLLECTION_OPENED]: ["collection_id", "courses_count"],
};

export const EMBED_ROUTE_NAMES = {
  HOME: "EmbedAcademyHome",
  COURSE: "EmbedAcademyCourseContent",
  COLLECTION: "EmbedAcademyCollectionContent",
  COLLECTION_COURSE: "EmbedAcademyCollectionCourseContent",
};

// No token means no catalog: if the parent does not answer `ready` within this window we show
// the error state instead of an endless spinner (typical case: opening /embed/academy outside an
// iframe).
export const EMBED_TOKEN_TIMEOUT_MS = 5000;

// How often `ready` is re-announced while waiting for the token, in case the parent's listener
// was not attached yet on the first announcement.
export const EMBED_ANNOUNCE_INTERVAL_MS = 500;

// Smallest height change reported to the parent. Avoids the two-value cycle when the content
// height depends on the frame height.
export const EMBED_HEIGHT_THRESHOLD_PX = 2;

// Error codes returned by the catalog service.
export const EMBED_CATALOG_ERRORS = {
  // Local to the frontend
  MISSING_TOKEN: "MISSING_TOKEN",
  NETWORK_ERROR: "NETWORK_ERROR",
  HANDSHAKE_TIMEOUT: "HANDSHAKE_TIMEOUT",
  // From the API (the body's `error` field)
  MISSING_AUTH_HEADER: "MISSING_AUTH_HEADER",
  INVALID_AUTH_FORMAT: "INVALID_AUTH_FORMAT",
  EMPTY_TOKEN: "EMPTY_TOKEN",
  INVALID_TOKEN: "INVALID_TOKEN",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  RATE_LIMITED: "RATE_LIMITED",
  CONFIG_ERROR: "CONFIG_ERROR",
  // 500 = our bug, 502 = Strapi is down. Both used to surface as CATALOG_UNAVAILABLE, so a legacy
  // bug stayed buried under someone else's diagnosis, with the frontend retrying on backoff and
  // nobody looking for it.
  INTERNAL_ERROR: "INTERNAL_ERROR",
  CATALOG_UNAVAILABLE: "CATALOG_UNAVAILABLE",
};

// Codes carried by the protocol's `error` channel. This is the contract with Horizon: its type
// union must hold exactly these. The service's internal codes are a superset (MISSING_TOKEN,
// NETWORK_ERROR), so the bridge translates before posting — sending one of the extras reaches the
// parent as something it cannot render.
//
// The list grew from 5 to 9 with the error contract settled by the API's own validation pass (the
// three malformed-header 401s plus INTERNAL_ERROR). All 9 are reconciled one-to-one with
// Horizon's type union (2026-08-19), where the code travels sanitised as a property of the
// `academy_unavailable_shown` event.
//
// Any change here has to be confirmed on the other side by hand: a code present in one repo and
// absent in the other is caught by no test, because each repo tests its own list. Without that
// reconciliation every failure mode (misaligned secret, Strapi down, rate limit, handshake
// timeout) collapses into one indistinguishable bucket in the analytics.
export const EMBED_ERROR_CODES = {
  MISSING_AUTH_HEADER: "MISSING_AUTH_HEADER",
  INVALID_AUTH_FORMAT: "INVALID_AUTH_FORMAT",
  EMPTY_TOKEN: "EMPTY_TOKEN",
  INVALID_TOKEN: "INVALID_TOKEN",
  CONFIG_ERROR: "CONFIG_ERROR",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  CATALOG_UNAVAILABLE: "CATALOG_UNAVAILABLE",
  RATE_LIMITED: "RATE_LIMITED",
  HANDSHAKE_TIMEOUT: "HANDSHAKE_TIMEOUT",
};

// Only TOKEN_EXPIRED is retried, and exactly once. Everything else is reported to the parent over
// the `error` channel: mapping every 401 to `token_expired` puts Horizon in a refresh loop against
// a throttled endpoint (INVALID_TOKEN = misaligned secret, CONFIG_ERROR = missing secret; neither
// is fixed by asking for another token).
export const EMBED_RETRYABLE_ERRORS = ["TOKEN_EXPIRED"];

// Transient errors: the same request may work later. The contract marks them retryable with
// backoff and until now **neither side** implemented it, so a one-second Strapi hiccup degraded
// the section until the user did something. The backoff lives here rather than in the parent
// because this is the side that holds the error context.
//
// Orthogonal to EMBED_RETRYABLE_ERRORS: that one asks the parent for a new token (exactly once),
// this one retries the same request. Two distinct mechanisms, no overlap.
export const EMBED_TRANSIENT_ERRORS = ["CATALOG_UNAVAILABLE", "RATE_LIMITED"];

// Waits between retries, in ms. Two retries, increasing: with a 429 in play, hammering the
// endpoint is exactly what the status asks you not to do. The length of this array is the number
// of retries.
export const EMBED_BACKOFF_MS = [800, 2400];
